/**
 * Day 44 — Mobile Staff Safety
 * Lone-worker check-in, SOS alert, and emergency contact quick actions.
 * SOS/check-in work online (direct) and offline (queued via SAFETY_EVENT).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, ShieldAlert, Siren, CheckCircle, AlertTriangle, Phone, MapPin, MapPinOff,
} from 'lucide-react';
import { isOnline } from '@/lib/offline/syncEngine';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';

export default function MobileSafetyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shiftId = searchParams.get('shiftId') || '';

  const [config, setConfig] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [sosMessage, setSosMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [online, setOnlineState] = useState(isOnline());

  useEffect(() => {
    api.get('/mobile/safety/config').then(r => setConfig(r.data?.data)).catch(() => {});
    api.get('/mobile/safety/emergency-contacts', { params: { shiftId: shiftId || undefined } })
      .then(r => setContacts(r.data?.data || [])).catch(() => {});
  }, [shiftId]);

  const getLocation = () => new Promise((resolve) => {
    if (!config?.location_sharing_enabled || !navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve({}), { timeout: 5000 });
  });

  const triggerSos = async () => {
    if (!window.confirm('Send an SOS alert to your coordinator/admin now?')) return;
    setBusy(true);
    const loc = await getLocation();
    const payload = { eventType: 'SOS', shiftId: shiftId || null, message: sosMessage || 'SOS', ...loc };
    try {
      if (online) {
        const r = await api.post('/mobile/safety/sos', {
          clientActionId: (window.crypto?.randomUUID?.() || `sos-${Date.now()}`),
          shiftId: shiftId || null, message: sosMessage || 'SOS', ...loc,
        });
        toast.success(`SOS sent to ${r.data?.data?.recipients || 0} responder(s)`);
      } else {
        await queueOfflineAction(ACTION_TYPES.SAFETY_EVENT, payload);
        toast.success('SOS queued — will send when back online');
      }
      setSosMessage('');
    } catch (err) {
      toast.error('Could not send SOS');
    } finally {
      setBusy(false);
    }
  };

  const checkIn = async (status) => {
    setBusy(true);
    const loc = await getLocation();
    const payload = { eventType: 'CHECK_IN', shiftId: shiftId || null, status, ...loc };
    try {
      if (online) {
        await api.post('/mobile/safety/check-in', {
          clientActionId: (window.crypto?.randomUUID?.() || `ci-${Date.now()}`),
          shiftId: shiftId || null, status, ...loc,
        });
      } else {
        await queueOfflineAction(ACTION_TYPES.SAFETY_EVENT, payload);
      }
      toast.success(status === 'OK' ? "Check-in recorded — you're marked safe"
                                    : 'Assistance requested — coordinator notified');
    } catch (err) {
      toast.error('Could not record check-in');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-10" data-testid="mobile-safety-page">
      <div className="sticky top-0 z-10 bg-primary text-primary-foreground px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary/80" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            <h1 className="text-lg font-bold">Staff Safety</h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* SOS */}
        {config?.sos_enabled !== false && (
          <Card className="border-red-500/40">
            <CardContent className="p-4 space-y-3">
              <Textarea placeholder="Optional: what's happening?" rows={2} className="resize-none"
                        value={sosMessage} onChange={(e) => setSosMessage(e.target.value)} data-testid="sos-message" />
              <Button variant="destructive" className="w-full h-16 text-lg font-bold"
                      onClick={triggerSos} disabled={busy} data-testid="sos-btn">
                <Siren className="h-6 w-6 mr-2" /> SEND SOS
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Alerts your coordinator and admins immediately.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Lone-worker check-in */}
        {config?.lone_worker_enabled !== false && (
          <Card data-testid="lone-worker-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Lone-worker check-in</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0 grid grid-cols-2 gap-3">
              <Button variant="outline" className="h-14 border-green-500/40" onClick={() => checkIn('OK')} disabled={busy} data-testid="checkin-ok-btn">
                <CheckCircle className="h-5 w-5 mr-2 text-green-600" /> I'm OK
              </Button>
              <Button variant="outline" className="h-14 border-amber-500/40" onClick={() => checkIn('NEED_ASSISTANCE')} disabled={busy} data-testid="checkin-help-btn">
                <AlertTriangle className="h-5 w-5 mr-2 text-amber-600" /> Need help
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Location sharing status */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="location-status">
          {config?.location_sharing_enabled
            ? <><MapPin className="h-4 w-4 text-blue-600" /> Location sharing is enabled for safety alerts (policy-approved).</>
            : <><MapPinOff className="h-4 w-4" /> Location sharing is off. Alerts are sent without your location.</>}
        </div>

        {/* Emergency contacts */}
        {contacts.length > 0 && (
          <Card data-testid="emergency-contacts-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Emergency contacts</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
              {contacts.map((c, i) => (
                <a key={i} href={`tel:${c.phone}`} className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/40" data-testid={`emergency-contact-${i}`}>
                  <div>
                    <p className="text-sm font-medium">{c.name || c.type}</p>
                    <p className="text-xs text-muted-foreground">{c.relationship || c.type} · {c.phone}</p>
                  </div>
                  <Phone className="h-5 w-5 text-green-600" />
                </a>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
